import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from "@shared/const";
import { QueryClient, type DehydratedState } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot, hydrateRoot } from "react-dom/client";
import superjson from "superjson";
import { AppProviders } from "./AppProviders";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } });
const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (error instanceof TRPCClientError && error.message === UNAUTHED_ERR_MSG) {
    window.location.href = getLoginUrl();
  }
};
queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    redirectToLoginIfUnauthorized(event.query.state.error);
    console.error("[API Query Error]", event.query.state.error);
  }
});
queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    redirectToLoginIfUnauthorized(event.mutation.state.error);
    console.error("[API Mutation Error]", event.mutation.state.error);
  }
});
const trpcClient = trpc.createClient({
  links: [httpBatchLink({
    url: "/api/trpc",
    transformer: superjson,
    fetch(input, init) { return globalThis.fetch(input, { ...(init ?? {}), credentials: "include" }); },
  })],
});
const rawState = (window as unknown as { __RQ_STATE__?: ReturnType<typeof superjson.serialize> }).__RQ_STATE__;
const state = rawState ? superjson.deserialize<DehydratedState>(rawState) : undefined;
const app = <AppProviders queryClient={queryClient} trpcClient={trpcClient} state={state}><App /></AppProviders>;
const root = document.getElementById("root")!;
if (root.hasChildNodes()) {
  hydrateRoot(root, app, { onRecoverableError: error => console.error("[Hydration]", error) });
} else {
  // A logged SSR failure still degrades to a working browser-rendered site.
  createRoot(root).render(app);
}
